import { useActiveCoachReviewsContext } from "../hooks/useActiveCoachReviewsContext";
import { useCoachReviewsStore } from "../stores/coach-reviews.store";
import { isJwtTokenExpired } from "../utils/jwt.utils";
import { useMutation } from "@tanstack/react-query";

const EXPIRATION_BUFFER_MS = 60_000; // 60 seconds buffer

export const useJwtAccessToken = () => {
	const { promises } = useActiveCoachReviewsContext();

	if (!promises) {
		throw new Error("Promises are not available");
	}

	const { mutateAsync: issueToken } = useMutation({
		mutationFn: () => promises.issueAccessToken(),
	});
	const storedAccessToken = useCoachReviewsStore((s) => s.accessToken);
	const setAccessToken = useCoachReviewsStore((s) => s.setAccessToken);

	const requestNewToken = async (): Promise<string> => {
		const data = await issueToken();
		const token = data?.data.token;

		if (!token) {
			throw new Error("useJwtAccessToken: mutation did not return an access token");
		}

		setAccessToken(token);
		return token;
	};

	const getToken = async (): Promise<string> => {
		// Check if the stored token is valid and not expired (with a time buffer)
		if (storedAccessToken && !isJwtTokenExpired(storedAccessToken, EXPIRATION_BUFFER_MS)) {
			return storedAccessToken;
		}

		return requestNewToken();
	};

	const clearToken = () => {
		setAccessToken(null);
	};

	return { getToken, clearToken };
};
